import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, Mail, MessageSquare, Save, AlertTriangle, CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface AlertSettings {
  email_notifications: boolean;
  email_addresses: string[];
  slack_webhook_url: string;
}

export default function AlertsPage() {
  const { profile } = useAuth();
  const [settings, setSettings] = useState<AlertSettings>({
    email_notifications: true,
    email_addresses: [],
    slack_webhook_url: '',
  });
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile?.tenant_id) {
      fetchAlertSettings();
    }
  }, [profile]);

  const fetchAlertSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('alert_settings')
        .select('*')
        .eq('tenant_id', profile.tenant_id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSettings({
          email_notifications: data.email_notifications,
          email_addresses: data.email_addresses || [],
          slack_webhook_url: data.slack_webhook_url || '',
        });
      }
    } catch (error) {
      console.error('Error fetching alert settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('alert_settings')
        .upsert({
          tenant_id: profile.tenant_id,
          email_notifications: settings.email_notifications,
          email_addresses: settings.email_addresses,
          slack_webhook_url: settings.slack_webhook_url || null,
        }, {
          onConflict: 'tenant_id'
        });

      if (error) throw error;

      toast({
        title: "Settings saved",
        description: "Alert settings have been updated successfully.",
      });
    } catch (error) {
      console.error('Error saving alert settings:', error);
      toast({
        title: "Error",
        description: "Failed to save alert settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    if (emailInput && emailInput.includes('@') && !settings.email_addresses.includes(emailInput)) {
      setSettings(prev => ({
        ...prev,
        email_addresses: [...prev.email_addresses, emailInput]
      }));
      setEmailInput('');
    }
  };

  const removeEmail = (email: string) => {
    setSettings(prev => ({
      ...prev,
      email_addresses: prev.email_addresses.filter(e => e !== email)
    }));
  };

  const alertRules = [
    {
      name: "Server Down",
      description: "Triggered when a server becomes unreachable",
      severity: "Critical",
      condition: "up == 0",
      enabled: true
    },
    {
      name: "High CPU Usage",
      description: "CPU usage above 90% for 5 minutes",
      severity: "Warning",
      condition: "cpu_usage > 90",
      enabled: true
    },
    {
      name: "Low Disk Space",
      description: "Disk usage above 90%",
      severity: "Warning",
      condition: "disk_usage > 90",
      enabled: true
    },
    {
      name: "High Memory Usage",
      description: "Memory usage above 95% for 10 minutes",
      severity: "Warning",
      condition: "memory_usage > 95",
      enabled: true
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Alerts & Notifications</h1>
        <div className="animate-pulse space-y-4">
          <Card>
            <CardHeader>
              <div className="h-6 bg-muted rounded w-48"></div>
              <div className="h-4 bg-muted rounded w-64"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="h-10 bg-muted rounded"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Alerts & Notificaties</h1>
          <p className="text-muted-foreground">
            Configureer hoe je op de hoogte wilt worden gehouden van server problemen
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="shadow-md hover:shadow-lg transition-all duration-200">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Opslaan..." : "Instellingen Opslaan"}
        </Button>
      </div>

      <Tabs defaultValue="notifications" className="w-full">
        <TabsList>
          <TabsTrigger value="notifications">Notification Settings</TabsTrigger>
          <TabsTrigger value="rules">Alert Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-6">
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Notificaties
              </CardTitle>
              <CardDescription>
                Configureer email alerts voor je team
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="email-notifications">Enable Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive alerts via email when issues occur
                  </p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={settings.email_notifications}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, email_notifications: checked }))
                  }
                />
              </div>

              {settings.email_notifications && (
                <div className="space-y-4">
                  <div>
                    <Label>Email Addresses</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="team@company.com"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                      />
                      <Button onClick={addEmail} type="button">Add</Button>
                    </div>
                  </div>

                  {settings.email_addresses.length > 0 && (
                    <div className="space-y-2">
                      <Label>Recipients</Label>
                      <div className="flex flex-wrap gap-2">
                        {settings.email_addresses.map((email) => (
                          <Badge
                            key={email}
                            variant="secondary"
                            className="cursor-pointer"
                            onClick={() => removeEmail(email)}
                          >
                            {email} ×
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Slack Integration
              </CardTitle>
              <CardDescription>
                Verstuur alerts naar je Slack kanalen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="slack-webhook">Slack Webhook URL</Label>
                <Input
                  id="slack-webhook"
                  placeholder="https://hooks.slack.com/services/..."
                  value={settings.slack_webhook_url}
                  onChange={(e) => 
                    setSettings(prev => ({ ...prev, slack_webhook_url: e.target.value }))
                  }
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Create a webhook URL in your Slack workspace settings
                </p>
              </div>

              {settings.slack_webhook_url && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Slack integration configured. Test alerts will be sent to verify the connection.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Alert Regels
              </CardTitle>
              <CardDescription>
                Voorgedefinieerde monitoring regels voor je servers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {alertRules.map((rule, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium">{rule.name}</h4>
                        <Badge 
                          variant={rule.severity === 'Critical' ? 'destructive' : 'default'}
                          className={rule.severity === 'Warning' ? 'bg-warning text-warning-foreground' : ''}
                        >
                          {rule.severity}
                        </Badge>
                        {rule.enabled && (
                          <Badge variant="outline" className="bg-success text-success-foreground">
                            Active
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{rule.description}</p>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{rule.condition}</code>
                    </div>
                    <Switch checked={rule.enabled} disabled />
                  </div>
                ))}
              </div>

              <Alert className="mt-6">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Alert rules are automatically configured based on industry best practices. 
                  Custom rules and thresholds will be available in the Pro plan.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}